// Cloudflare Worker API client — HANDOFF schema Section 7.
// Every call carries X-Study-Key. Researcher-only calls also carry
// X-Researcher-Token.

const WORKER_URL = import.meta.env.VITE_WORKER_URL;
const STUDY_KEY = import.meta.env.VITE_STUDY_KEY;

class WorkerApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'WorkerApiError';
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = 'GET', body, researcherToken, headers = {} } = {}) {
  const res = await fetch(`${WORKER_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Study-Key': STUDY_KEY,
      ...(researcherToken ? { 'X-Researcher-Token': researcherToken } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let parsed = null;
    try {
      parsed = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new WorkerApiError(parsed?.error ?? `Request failed (${res.status})`, res.status, parsed);
  }
  return res.json();
}

export async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url} (${res.status})`);
  return res.json();
}

export function signUpload({ participantId, sessionId, files }) {
  return request('/sign-upload', {
    method: 'POST',
    body: { participant_id: participantId, session_id: sessionId, files },
  });
}

/**
 * A short, human-readable name for this device, so the researcher panel can
 * say which phone push is registered to. Derived from the user agent because
 * there's nothing better available to a web app — it only has to be good
 * enough to tell an iPhone from an Android in a two-device pilot.
 */
export function describeDevice() {
  const ua = navigator.userAgent || '';
  let os = 'Unknown device';
  if (/iPhone|iPad|iPod/.test(ua)) os = /iPad/.test(ua) ? 'iPad' : 'iPhone';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Macintosh/.test(ua)) os = 'Mac';
  else if (/Windows/.test(ua)) os = 'Windows';

  let browser = '';
  if (/CriOS/.test(ua)) browser = 'Chrome';
  else if (/FxiOS/.test(ua)) browser = 'Firefox';
  else if (/EdgiOS|Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;
  return `${os}${browser ? ` · ${browser}` : ''}${standalone ? ' · installed' : ' · browser tab'}`;
}

export function registerPush({ participantId, fcmToken, schedule }) {
  return request('/register-push', {
    method: 'POST',
    body: {
      participant_id: participantId,
      fcm_token: fcmToken,
      schedule,
      device_label: describeDevice(),
    },
  });
}

export function fetchResearcherState({ participantId, researcherToken }) {
  return request(`/researcher/participant-state?participant_id=${encodeURIComponent(participantId)}`, {
    researcherToken,
  });
}

export function fetchContentManifest() {
  return request('/content-manifest');
}

export function verifyPin(pin) {
  return request('/verify-pin', { method: 'POST', body: { pin } });
}

export function updateResearcherState({ participantId, sessionNumber, contentIndex, schedule, researcherToken }) {
  return request('/researcher/participant-state', {
    method: 'PATCH',
    researcherToken,
    body: {
      participant_id: participantId,
      session_number: sessionNumber,
      content_index: contentIndex,
      schedule,
    },
  });
}

export { WorkerApiError };
